import React from "react";
import { useNavigate } from "react-router-dom";
import type { AdminTabId } from "../AdminWorkspace/adminTabs";
import type { UserTabId } from "../UserWorkspace/userTabs";
import type { ItCheck } from "../../api/itCheck";
import { RequestTablePagination } from "../RequestTable/RequestTablePagination";
import { formatScheduleDateTime, getNearestNextRunAt } from "../../utils/nearestScheduleRun";
import { formatTaskSchedulePeriodicity } from "../../utils/formatSchedulePeriodicity";
import "../RequestTable/RequestTable.scss";

interface Props {
  data: ItCheck[];
  page: number;
  pageSize: number;
  returnTab?: AdminTabId | UserTabId;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const statusLabel = (status?: string): string => {
  switch (status) {
    case "error":
      return "Ошибка";
    case "new":
      return "Новая";
    case "scheduled":
      return "Запланирована";
    case "processing":
    case "in_progress":
      return "В обработке";
    case "finished":
      return "Завершена";
    case "done":
      return "Готово";
    default:
      return status ?? "—";
  }
};

export const ItCheckTable: React.FC<Props> = ({
  data,
  page,
  pageSize,
  returnTab,
  onPageChange,
  onPageSizeChange,
}) => {
  const navigate = useNavigate();
  const hasNext = data.length >= pageSize;
  const totalPages = hasNext ? page + 2 : page + 1;

  return (
    <div className="request-table-wrapper">
      <div className="request-table-card">
        <table className="request-table">
          <thead>
            <tr>
              <th>Продукт</th>
              <th>URL</th>
              <th>Запланированная проверка</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={4} className="request-table-empty">
                  Нет данных
                </td>
              </tr>
            ) : (
              data.map((item) => {
                const nearest = getNearestNextRunAt(item.schedule);
                const period = formatTaskSchedulePeriodicity(item.schedule);
                return (
                  <tr
                    key={item.id}
                    className="request-table-row-clickable"
                    onClick={() => {
                      if (!item.id) return;
                      navigate(
                        `/it-check/${item.id}`,
                        returnTab ? { state: { returnTab } } : undefined
                      );
                    }}
                  >
                    <td>{item.productName}</td>
                    <td>
                      <div className="request-table-urls">
                        <span className="request-table-link">{item.url}</span>
                      </div>
                    </td>
                    <td>
                      {period || nearest ? (
                        <div className="request-table-scheduled-cell">
                          {period ? (
                            <span className="request-table-schedule-period">{period}</span>
                          ) : null}
                          {nearest ? (
                            <span className="request-table-scheduled">
                              {formatScheduleDateTime(nearest)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="request-table-scheduled request-table-scheduled--empty">
                          —
                        </span>
                      )}
                    </td>
                    <td>
                      {item.status ? (
                        <span className="request-table-status">{statusLabel(item.status)}</span>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <RequestTablePagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        canGoNext={hasNext}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};
